import Link from 'next/link';
import Image from 'next/image';
import { format, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';
import { Evento } from '@/types';
import { LABELS } from '@/lib/labels';

interface Props {
  evento: Evento;
}

interface StatoBadge {
  label: string;
  classes: string;
}

function getStatoImg(evento: Evento): string {
  if (evento.inEvidenza) {
    return '/images/icons/in-evidenza.webp';
  }
  if (evento.stato === 'non-attivo') {
    return '/images/icons/non-attivo.webp';
  }
  return '';
}
function getStatoBadge(evento: Evento): StatoBadge {
  switch (evento.stato) {
    case 'confermato':
      return { label: LABELS.calendario.legenda.attiva.label, classes: 'bg-accent-green text-white' };
    case 'non-attivo':
      return { label: LABELS.calendario.legenda.nonAttivo.label, classes: 'bg-gray-400 text-white' };
    case 'in-aggiornamento':
    default:
      return { label: LABELS.calendario.legenda.inAggiornamento.label, classes: 'bg-accent-orange text-white' };
  }
}

export default function CalendarioItem({ evento }: Props) {
  const stato = getStatoBadge(evento);
  const isNonAttivo = evento.stato === 'non-attivo';
  const isInEvidenza = evento.inEvidenza === true;

  const date = parseISO(evento.data);
  const dayNum = date.getDate();
  const monthAbbr = format(date, 'MMM', { locale: it }).toUpperCase().replace('.', '');
  const weekday = format(date, 'EEEE', { locale: it }).toUpperCase();

  // Stili condizionali per i diversi stati
  const articleClass = isNonAttivo
    ? 'bg-gray-100 rounded-2xl border border-gray-200 shadow-sm overflow-hidden opacity-70'
    : isInEvidenza
      ? 'bg-orange-50 rounded-2xl border border-orange-300 shadow-md overflow-hidden transition-shadow hover:shadow-lg cursor-pointer ring-1 ring-orange-300'
      : 'bg-board-light rounded-2xl border border-chalk/15 shadow-sm overflow-hidden transition-shadow hover:shadow-md cursor-pointer';

  const dateColClass = isNonAttivo
    ? 'w-[100px] flex-shrink-0 bg-gray-200 flex flex-col items-center justify-center py-4 px-1 text-center gap-0.5 border-r border-gray-300'
    : isInEvidenza
      ? 'w-[100px] flex-shrink-0 bg-orange-400 flex flex-col items-center justify-center py-4 px-1 text-center gap-0.5 border-r border-orange-500'
      : 'w-[100px] flex-shrink-0 bg-section-calGreen flex flex-col items-center justify-center py-4 px-1 text-center gap-0.5 border-r border-black/10';

  const dateTextClass = isNonAttivo ? 'text-gray-500' : isInEvidenza ? 'text-white' : 'text-chalk';
  const arrowClass = isInEvidenza ? 'text-orange-500' : 'text-accent-orange';

  const inner = (
    <article className={articleClass}>
      <div className="flex items-stretch">

        {/* Colonna data */}
        <div className={dateColClass}>
          <span className={`font-section text-[13px] uppercase tracking-wider font-bold leading-none ${dateTextClass}`}>
            {weekday}
          </span>
          <span className={`font-section text-4xl md:text-5xl font-bold leading-none ${dateTextClass}`}>
            {dayNum}
          </span>
          <span className={`font-section text-[13px] uppercase tracking-wider font-bold leading-none ${dateTextClass}`}>
            {monthAbbr}
          </span>
        </div>

        {/* Icona */}
        {evento.inEvidenza || evento.stato === 'non-attivo' ? (
          <div className="pl-4 flex-shrink-0 flex items-center justify-center w-20" aria-hidden="true">
            <Image src={getStatoImg(evento)} alt={`Icona stato serata: ${stato.label}`} width={80} height={80} className="object-contain" />
          </div>
        ) : (
          <div className="flex-shrink-0 flex items-center justify-center w-8" aria-hidden="true">
          </div>
        )}

        {/* Contenuto principale */}
        <div className="flex-1 min-w-0 px-4 py-3">
          <div className="flex flex-col items-end sm:flex-row sm:items-start sm:justify-between sm:gap-2">
            <h3 className={`order-2 sm:order-1 w-full sm:flex-1 sm:min-w-0 font-section font-bold text-sm sm:text-base leading-tight uppercase tracking-wide ${isNonAttivo ? 'text-gray-400' : 'text-chalk'}`}>
              {evento.titolo}
            </h3>
            <span className={`order-1 sm:order-2 flex-shrink-0 text-[9px] font-bold px-2 py-0.5 rounded-full leading-tight ${stato.classes}`}>
              {stato.label}
            </span>
          </div>
          <p className={`text-xs mt-1 line-clamp-2 leading-snug ${isNonAttivo ? 'text-gray-400' : 'text-chalk'}`}>
            {evento.descrizioneBreve}
          </p>

          <div className={`flex flex-col gap-0.5 mt-3 text-xs ${isNonAttivo ? 'text-gray-400' : 'text-chalk'}`}>
            <span className="flex items-center gap-1">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-3 h-3 flex-shrink-0" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              {evento.orario}{evento.orarioFine ? ` – ${evento.orarioFine}` : ''}
            </span>
            <span className="flex items-center gap-1">
              <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3 flex-shrink-0" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/></svg>
              {LABELS.evento.doveValore}
            </span>
          </div>
        </div>

        {/* Freccia navigazione */}
        {evento.pubblicato && (
          <div className={`flex-shrink-0 flex items-center pr-3 sm:px-3 text-2xl font-bold select-none ${arrowClass}`} aria-hidden="true">
            ›
          </div>
        )}

      </div>
    </article>
  );

  if (evento.pubblicato) {
    return (
      <Link
        href={`/eventi/${evento.slug}/`}
        className="block focus:outline-none focus:ring-2 focus:ring-accent-gold rounded-2xl"
        aria-label={`Vai alla pagina evento: ${evento.titolo}`}
      >
        {inner}
      </Link>
    );
  }

  return inner;
}
